import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import MatchCard from "@/components/MatchCard";
import LeagueSwitcher from "@/components/LeagueSwitcher";
import AdSlot from "@/components/AdSlot";
import { fetchMatches } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

export default function Matches() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [league, setLeague] = useState("all");
  const [status, setStatus] = useState("all");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");

  const STATUSES = [
    { id: "all", label: t("filter.all") },
    { id: "live", label: t("filter.live") },
    { id: "upcoming", label: t("filter.upcoming") },
    { id: "finished", label: t("filter.finished") },
  ];

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (league !== "all") params.league = league;
    if (status !== "all") params.status = status;
    fetchMatches(params)
      .then((d) => setMatches(d))
      .finally(() => setLoading(false));
  }, [league, status]);

  // sync query to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (query) params.set("q", query);
    else params.delete("q");
    setSearchParams(params, { replace: true });
  }, [query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return matches;
    return matches.filter((m) => {
      const hay = [
        m.home_team?.name,
        m.away_team?.name,
        m.home_team?.short,
        m.away_team?.short,
        m.league,
        m.venue,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query, matches]);

  return (
    <div data-testid="matches-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-white/10 pb-6">
        <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">{t("matches.title")}</h1>
        <p className="text-sm text-white/50 mt-2">{t("matches.subtitle")}</p>
      </div>

      <div className="mt-6 space-y-4">
        {/* Search bar */}
        <div className="flex items-center gap-3 border border-white/15 px-4 py-2.5 focus-within:border-volt transition-colors">
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            data-testid="matches-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("matches.search_placeholder")}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="matches-search-clear"
              className="text-xs uppercase tracking-[0.18em] text-white/40 hover:text-volt"
            >
              {t("search.clear")}
            </button>
          )}
        </div>

        <LeagueSwitcher value={league} onChange={setLeague} />
        <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="status-filter">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              data-testid={`status-${s.id}`}
              className={`shrink-0 px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                status === s.id
                  ? "bg-white text-black border-white font-bold"
                  : "bg-transparent text-white/70 border-white/15 hover:border-white/40"
              }`}
            >
              {s.id === "live" && <span className="live-dot mr-2 align-middle" />}
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm uppercase tracking-[0.2em]">{t("matches.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-white/40 py-20 text-sm uppercase tracking-[0.2em]" data-testid="no-matches">
            {t("matches.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matches-grid">
            {filtered.map((m, i) => (
              <React.Fragment key={m.id}>
                <MatchCard match={m} />
                {i === 5 && (
                  <div className="md:col-span-2 lg:col-span-3">
                    <AdSlot
                      testId="ad-matches-infeed"
                      slot=""
                      format="auto"
                      className="w-full"
                      style={{ minHeight: 90 }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
